import { useContext } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Alert } from '@mui/material'
import AppFormContainer from 'components/forms/AppFormContainer'
import FetchContext from '../../context/fetch/FetchContext'
import Page from '../pages/Page'
import Section from '../containers/Section'
import useContactPageValidations from '../contacts/useContactPageValidations'
import useOptimisticList from '../contacts/useOptimisticList'
import { addressFieldLabelOf, detailFieldLabelOf } from '../contacts/contactLabels'
import { toAddressBody, toDetailBody } from '../contacts/contactLists'
import FirmContactSettings from './FirmContactSettings'

const SETTINGS_PATH = '/users/me/firm/settings'

const SETTINGS_QUERY_KEY = ['firm-settings']

const DETAILS_LENS = {
    get: (cached) => cached?.data?.contact?.details ?? [],
    set: (cached, details) => ({
        ...cached,
        data: {
            ...cached.data,
            contact: {
                ...cached.data?.contact,
                details,
            },
        },
    }),
}

const ADDRESSES_LENS = {
    get: (cached) => cached?.data?.contact?.addresses ?? [],
    set: (cached, addresses) => ({
        ...cached,
        data: {
            ...cached.data,
            contact: {
                ...cached.data?.contact,
                addresses,
            },
        },
    }),
}

// Edits the firm the signed-in user belongs to. The firm is resolved from the token, so
// this page never names one.
export default function FirmSettingsPage() {
    const fetcher = useContext(FetchContext)

    const { data: loaded, isError, isFetching } = useQuery({
        queryKey: SETTINGS_QUERY_KEY,
        queryFn: () => fetcher.fetchOwnFirmSettings(),
        enabled: fetcher.isAuthenticated(),
    })

    const { locked, showRefusal } = useContactPageValidations(loaded?.meta?.validations)

    const detailsList = useOptimisticList({
        queryKey: SETTINGS_QUERY_KEY,
        lens: DETAILS_LENS,
        path: `${SETTINGS_PATH}/details`,
        positionsPath: `${SETTINGS_PATH}/detail-positions`,
        scope: 'own-firm:details',
        toInput: toDetailBody,
        labelOf: detailFieldLabelOf,
        onRefused: (error) => showRefusal(error, { labelOf: detailFieldLabelOf }),
    })

    const addressesList = useOptimisticList({
        queryKey: SETTINGS_QUERY_KEY,
        lens: ADDRESSES_LENS,
        path: `${SETTINGS_PATH}/addresses`,
        positionsPath: `${SETTINGS_PATH}/address-positions`,
        scope: 'own-firm:addresses',
        toInput: toAddressBody,
        labelOf: addressFieldLabelOf,
        onRefused: (error) => showRefusal(error, { labelOf: addressFieldLabelOf }),
    })

    return (
        <Page
            className="firm-settings"
            title="הגדרות המשרד"
        >
            <AppFormContainer>
                {isError
                    ? (
                        <Section title="פרטי קשר וכתובות" xs={12}>
                            <Alert severity="info" data-testid="firm-settings-no-firm">לא נמצא משרד לעריכה</Alert>
                        </Section>
                    )
                    : (
                        <FirmContactSettings
                            detailsList={detailsList}
                            addressesList={addressesList}
                            locked={locked}
                            consentPathOf={(detailId) => `${SETTINGS_PATH}/details/${detailId}/consent`}
                            busy={isFetching}
                        />
                    )}
            </AppFormContainer>
        </Page>
    )
}
